import DOMPurify from 'dompurify'
import { get } from '../vue_shared/http'
import helpers from './helpers'
import i18nData from './prizmdoc/languages/en-US.json'
import { getPrizmdocBaseUrl } from '../vue_shared/api'
import loadPrizmdocAssets from './prizmdoc-assets'
import _ from 'lodash'

async function fetchHtmlTemplates(): Promise<object> {
  const templateModules = import.meta.glob('./prizmdoc/templates/*.html', {
    query: '?raw',
    import: 'default'
  })

  const htmlTemplates = {
    comment: null,
    contextMenu: null,
    copyOverlay: null,
    downloadOverlay: null,
    esignOverlay: null,
    hyperlinkMenu: null,
    imageStampOverlay: null,
    markDelete: null,
    overwriteOverlay: null,
    pageRedactionOverlay: null,
    printOverlay: null,
    print: null,
    redactionReason: null,
    unsavedChangesOverlay: null,
    viewer: null
  }

  const resolvedTemplates = await Promise.all(
    Object.keys(htmlTemplates).map(async (template) => {
      const path = `./prizmdoc/templates/${template}Template.html`
      if (templateModules[path]) {
        const content = await templateModules[path]()
        return { [template]: content }
      }
      return { [template]: null }
    })
  )

  return resolvedTemplates.reduce(
    (acc: object, cur: object) => ({ ...acc, ...cur }),
    {}
  ) as object
}

function isAgencyStaff(currentUser) {
  const user = currentUser || getUserFromHeader()

  return user?.user_role !== 'guest'
}

function getUserFromHeader() {
  const el = document.querySelector(
    '#app-header-legacy .mount'
  ) as HTMLElement | null

  try {
    return el?.dataset.user ? JSON.parse(el.dataset.user) : undefined
  } catch {
    return undefined
  }
}

async function fetchRegexes(currentUser) {
  if (!isAgencyStaff(currentUser)) {
    return undefined
  }
  const response = (await get('/admin/redaction_regexes')) as any
  const regexes = response?.data
  const redactionRegexes = {
    highlightColor: '#ffff00',
    searchOnInit: false,
    globalOptions: {
      matchCase: false,
      endsWith: false,
      beginsWith: false,
      matchWholeWord: false,
      exactPhrase: false
    },
    terms: []
  }
  if (regexes) {
    // eslint-disable-next-line camelcase
    redactionRegexes.terms = regexes.map(({ regex_name, regex }) => ({
      searchTermIsRegex: true,
      // eslint-disable-next-line camelcase
      searchTerm: regex_name,
      // eslint-disable-next-line camelcase
      description: regex_name,
      userDefinedRegex: regex,
      highlightColor: '#ffff00',
      selected: false
    }))
  }
  return redactionRegexes
}

async function fetchRedactionReasons(): Promise<any> {
  return get('/admin/redaction_reasons').then(({ data: reasons }: any) => ({
    autoApplyDefaultReason: true,
    enableRedactionReasonSelection: true,
    enableFreeformRedactionReasons: true,
    maxLengthFreeformRedactionReasons: 80,
    reasons:
      reasons?.length > 0
        ? reasons.map(({ name }) => ({
            reason: _.escape(name)
          }))
        : [
            { reason: 'Client Privilege' },
            { reason: 'Privacy Information' },
            { reason: 'Redacted' }
          ]
  }))
}

async function openRedactionTab(): Promise<any> {
  const redactTabItem = document.querySelector('[data-pcc-nav-tab=redact]')
  if (redactTabItem) {
    redactTabItem.classList.add('pcc-active')
    const pccTabPane = redactTabItem.querySelector('.pcc-tab-pane')
    if (pccTabPane) {
      pccTabPane.classList.add('pcc-open')
    }
  }
}

async function closeViewTab(): Promise<any> {
  const viewTabItem = document.querySelector('[data-pcc-nav-tab=view]')
  if (viewTabItem) {
    viewTabItem.classList.remove('pcc-active')
    const pccTabPane = viewTabItem.querySelector('.pcc-tab-pane')
    if (pccTabPane) {
      pccTabPane.classList.remove('pcc-open')
    }
  }
}

async function showRedactionTab(selector: string): Promise<any> {
  // used for redaction show
  $(selector).on('ViewerReady', function () {
    openRedactionTab()
    closeViewTab()
  })
  // used for rapid review
  // @ts-ignore: pccViewer is defined in `prizmdoc/viewer.js`.
  if ($(selector).pccViewer()) {
    openRedactionTab()
    closeViewTab()
  }
}

export default async function PrizmdocViewer(currentUser = null): Promise<any> {
  const [htmlTemplates, regexes, redactionReasons] = await Promise.all([
    fetchHtmlTemplates(),
    fetchRegexes(currentUser),
    fetchRedactionReasons(),
    loadPrizmdocAssets()
  ])

  return async (
    selector: string,
    viewingSessionId: string,
    documentTitle: string,
    redaction: boolean,
    reviewState: string | null,
    nextRequest: object
  ) => {
    // @ts-ignore: pccViewer is defined in `prizmdoc/viewer.js`.
    const viewerControl = $(selector).pccViewer({
      nextRequest,
      annotationsMode: 'LayeredAnnotations',
      commentsPanelMode: 'full',
      documentID: viewingSessionId,
      documentDisplayName: DOMPurify.sanitize(documentTitle),
      icons: 'fake',
      imageHandlerUrl: getPrizmdocBaseUrl(),
      immediateActionMenuMode: 'on',
      immediateActionMenuActionsFilter: {
        comment: false,
        select: true,
        copy: true,
        highlight: false,
        redact: true,
        hyperlink: false,
        strikethrough: false,
        cancel: true,
        delete: true
      },
      language: i18nData,
      predefinedSearch: regexes,
      redactionReasons,
      signatureCategories: 'Signature,Initials,Title',
      template: htmlTemplates,
      uiElements: helpers.buildUiElements(
        reviewState,
        redaction,
        // eslint-disable-next-line dot-notation
        nextRequest['rapidReview']
      ),
      viewerAssetsPath: '/viewer-assets'
    }).viewerControl
    if (redaction && reviewState !== 'Ready') {
      showRedactionTab(selector)
    }
    return viewerControl
  }
}
